import React, { useEffect, useState, useRef } from 'react';
import { ShieldCheckIcon, SearchIcon, UploadIcon, SearchXIcon } from 'lucide-react';
import { Document, ViewerState } from '../types/documents';
import { DocumentList } from './DocumentList';
import { EmptyState } from './EmptyState';
import { RestrictedState } from './RestrictedState';
import { LoadingState } from './LoadingState';
import { DocumentPreview } from './DocumentPreview';
interface SecureDocumentViewerProps {
  documents: Document[];
  viewerState: ViewerState;
}
export function SecureDocumentViewer({
  documents,
  viewerState
}: SecureDocumentViewerProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [showUploadZone, setShowUploadZone] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const filteredDocuments = documents.filter(doc => doc.name.toLowerCase().includes(searchQuery.toLowerCase()) || doc.type.toLowerCase().includes(searchQuery.toLowerCase()));
  const handleViewDocument = (doc: Document) => {
    setSelectedDocument(doc);
  };
  const handleClosePreview = () => {
    setSelectedDocument(null);
  };
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      // In a real app, this would upload the files
      alert(`Selected ${files.length} file(s) for upload: ${Array.from(files).map(f => f.name).join(', ')}`);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      alert(`Dropped ${files.length} file(s) for upload: ${Array.from(files).map(f => f.name).join(', ')}`);
    }
  };
  // Determine what to render in the body
  const renderBody = () => {
    switch (viewerState) {
      case 'loading':
        return <LoadingState data-id="element-98" />;
      case 'restricted':
        return <RestrictedState data-id="element-99" />;
      case 'empty':
        return <EmptyState data-id="element-100" />;
      case 'default':
      case 'preview':
        if (filteredDocuments.length === 0 && searchQuery) {
          return <div className="flex flex-col items-center justify-center py-12 px-4 text-center" data-id="element-101">
              <div className="bg-[#F3F3F3] p-3 rounded-full mb-3" data-id="element-102">
                <SearchXIcon className="w-6 h-6 text-[#706E6B]" aria-hidden="true" data-id="element-103" />
              </div>
              <h3 className="text-sm font-semibold text-[#181818] mb-1" data-id="element-104">
                No results found
              </h3>
              <p className="text-sm text-[#706E6B]" data-id="element-105">
                No documents match "
                <span className="font-medium text-[#444444]" data-id="element-106">
                  {searchQuery}
                </span>
                "
              </p>
            </div>;
        }
        return <DocumentList documents={filteredDocuments} onViewDocument={handleViewDocument} data-id="element-107" />;
      default:
        return <EmptyState data-id="element-108" />;
    }
  };
  const isPreviewOpen = (viewerState === 'preview' || selectedDocument !== null) && viewerState !== 'restricted' && viewerState !== 'loading' && viewerState !== 'empty';
  useEffect(() => {
    if (viewerState === 'preview' && !selectedDocument && documents.length > 0) {
      setSelectedDocument(documents[0]);
    } else if (viewerState !== 'preview' && viewerState !== 'default') {
      setSelectedDocument(null);
    }
  }, [viewerState, documents, selectedDocument]);
  return <div className="bg-white rounded-lg shadow-[0_2px_4px_rgba(0,0,0,0.1)] border border-[#DDDBDA] overflow-hidden flex flex-col w-full" data-id="element-109">
      {/* Header */}
      <div className="px-6 py-4 border-b border-[#DDDBDA] bg-white flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-110">
        <div className="flex items-center space-x-3" data-id="element-111">
          <div className="bg-[#E3F2FD] p-2 rounded" data-id="element-112">
            <ShieldCheckIcon className="w-6 h-6 text-[#0176D3]" aria-hidden="true" data-id="element-113" />
          </div>
          <div data-id="element-114">
            <h2 className="text-lg font-semibold text-[#181818] leading-tight" data-id="element-115">
              Secure Document Viewer
            </h2>
            <p className="text-sm text-[#706E6B] mt-0.5" data-id="element-116">
              Manage and view sensitive loan documents securely
            </p>
          </div>
        </div>

        {/* Actions */}
        {(viewerState === 'default' || viewerState === 'preview') && <div className="flex items-center gap-3" data-id="element-117">
            <div className="relative w-full sm:w-56" data-id="element-118">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-119">
                <SearchIcon className="h-4 w-4 text-[#706E6B]" aria-hidden="true" data-id="element-120" />
              </div>
              <input type="text" placeholder="Search documents..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="block w-full pl-9 pr-3 py-2 border border-[#DDDBDA] rounded-md leading-5 bg-white placeholder-[#706E6B] focus:outline-none focus:ring-1 focus:ring-[#0176D3] focus:border-[#0176D3] sm:text-sm transition-colors" data-id="element-121" />
            </div>
            <input ref={fileInputRef} type="file" multiple accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" className="hidden" onChange={handleFileChange} data-id="element-122" />
            <button onClick={handleUploadClick} className="flex items-center px-4 py-2 text-sm font-medium text-white bg-[#0176D3] rounded-md hover:bg-[#015BA1] focus:outline-none focus:ring-2 focus:ring-[#0176D3] focus:ring-offset-1 transition-colors whitespace-nowrap" data-id="element-123">
              <UploadIcon className="w-4 h-4 mr-1.5" aria-hidden="true" data-id="element-124" />
              Upload
            </button>
          </div>}
      </div>

      {/* Upload Drop Zone */}
      {(viewerState === 'default' || viewerState === 'preview') && <div onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} onClick={handleUploadClick} className={`mx-6 mt-4 mb-2 border-2 border-dashed rounded-lg py-4 px-4 flex flex-col items-center justify-center cursor-pointer transition-colors ${isDragging ? 'border-[#0176D3] bg-[#E3F2FD]' : 'border-[#DDDBDA] bg-[#FAFAF9] hover:border-[#0176D3]/50 hover:bg-[#F3F3F3]'}`} data-id="element-125">
          <UploadIcon className={`w-5 h-5 mb-1.5 ${isDragging ? 'text-[#0176D3]' : 'text-[#706E6B]'}`} aria-hidden="true" data-id="element-126" />
          <p className="text-sm text-[#444444] font-medium" data-id="element-127">
            {isDragging ? 'Drop files here' : 'Drag & drop files here, or click to browse'}
          </p>
          <p className="text-xs text-[#706E6B] mt-0.5" data-id="element-128">
            Supports PDF, DOC, DOCX, JPG, PNG
          </p>
        </div>}

      {/* Body */}
      <div className="flex-1 bg-white min-h-[200px]" data-id="element-129">{renderBody()}</div>

      {/* Preview Modal Overlay */}
      {isPreviewOpen && selectedDocument && <DocumentPreview document={selectedDocument} onClose={handleClosePreview} data-id="element-130" />}
    </div>;
}